import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Card,
  Flex,
  Group,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import Popover from "../Popover/Popover";

import { EmptyState, TextSerif } from "@/components/elements";
import { MakeCard } from "@/components/elements/MakeCard";
import { paths } from "@/config/paths";
import { defaultCurrency } from "@/lib/currency";
import { useGarageCars } from "@/services/cars/get-garage-cars";
import { useGarages } from "@/services/garages/get-garages";
import { Currency } from "@/types/currency";
import { formatCurrency, formatPercentage } from "@/utils/formatEuro";

import type { Garage } from "@/types/garage";
import type { FC } from "react";

interface DashboardGarageCarsProps {
  openCreateGarageModal: () => void;
  openCreateCarModal: (garageId: string) => void;
}

export const DashboardGarageCars: FC<DashboardGarageCarsProps> = ({
  openCreateCarModal,
  openCreateGarageModal,
}) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const isMobile = useMatches({ base: true, sm: false });
  const [popoverOpened, setPopoverOpened] = useState(false);

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { data, isLoading: isGaragesLoading } = useGarages({});
  const garages = data?.results || [];

  const [selectedGarageId, setSelectedGarageId] = useState<string>("");

  useEffect(() => {
    garages.length && setSelectedGarageId(garages[0].identifier);
  }, [garages]);

  const { data: garageCars, isLoading: isGarageCarsLoading } = useGarageCars({
    garageId: selectedGarageId,
    page: 1,
  });

  const cars = garageCars?.results || [];

  const isGaragesEmpty = garages.length === 0;
  const isGarageCarsEmpty = cars.length === 0;

  const textColor = isDark ? "#a7a7a7A" : "secondary.4";

  const fontSize = { base: "xs", sm: "sm" };

  return (
    <Card flex={1} mah="22.5em">
      <Stack>
        <Flex justify="space-between" align="center" w="100%">
          <Title order={3} fz="h4" mb="md">
            {t("dashboard.cars.title")}
          </Title>

          {garages.length > 1 ? (
            <Flex gap="lg" align="center" key={`${selectedGarageId}`}>
              {!isMobile ? (
                <Title order={3} fz="md" c={isDark ? "primary.2" : "primary.7"}>
                  {garages.find((garage) => garage.identifier === selectedGarageId)?.data.name}
                </Title>
              ) : null}

              {!isMobile ? (
                <Menu withinPortal position="bottom-end">
                  <Menu.Target>
                    <Box component="span" display="inline-block">
                      <ActionIcon
                        variant="light"
                        color="primary"
                        aria-label={t("common.open_menu")}
                      >
                        <IconChevronDown size={16} />
                      </ActionIcon>
                    </Box>
                  </Menu.Target>
                  <Menu.Dropdown miw={200} py="sm" px="md">
                    <Skeleton visible={isGaragesLoading}>
                      {garages.map((garage: Garage) => (
                        <Menu.Item
                          key={`garage-${garage.identifier}`}
                          p="sm"
                          onClick={() => setSelectedGarageId(garage.identifier)}
                        >
                          {garage.data.name || ""}
                        </Menu.Item>
                      ))}
                    </Skeleton>
                  </Menu.Dropdown>
                </Menu>
              ) : (
                <Popover
                  title={t("dashboard.cars.select_garage")}
                  actionInPending={isGarageCarsLoading}
                  popoverOpened={popoverOpened}
                  setPopoverOpened={setPopoverOpened}
                  target={
                    <ActionIcon
                      variant="light"
                      color="primary"
                      aria-label={t("dashboard.cars.select_garage")}
                      onClick={() => setPopoverOpened(!popoverOpened)}
                    >
                      <IconChevronDown size={16} />
                    </ActionIcon>
                  }
                >
                  <Skeleton visible={isGaragesLoading}>
                    <Menu>
                      {garages.map((garage: Garage) => (
                        <Menu.Item
                          key={`garage-${garage.identifier}`}
                          p="sm"
                          onClick={() => {
                            setSelectedGarageId(garage.identifier);
                            setPopoverOpened(false);
                          }}
                          style={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: isDark
                                ? "var(--mantine-color-dark-4)"
                                : "var(--mantine-color-gray-0)",
                            },
                            borderRadius: "var(--mantine-radius-default)",
                          }}
                        >
                          <Text size="sm">{garage.data.name || ""}</Text>
                        </Menu.Item>
                      ))}
                    </Menu>
                  </Skeleton>
                </Popover>
              )}
            </Flex>
          ) : null}
        </Flex>

        <Box>
          {isGaragesEmpty ? (
            <EmptyState type="noCars" text={t("dashboard.cars.empty_message")}>
              <Button
                leftSection={<IconPlus />}
                onClick={openCreateGarageModal}
                aria-label={t("dashboard.garage.create_button")}
              >
                {t("dashboard.garage.create_button")}
              </Button>
            </EmptyState>
          ) : !isGarageCarsLoading && isGarageCarsEmpty ? (
            <EmptyState type="noCars" text={t("dashboard.cars.empy_garage")}>
              <Button
                leftSection={<IconPlus />}
                onClick={() => openCreateCarModal(selectedGarageId)}
                aria-label={t("dashboard.cars.create_button")}
              >
                {t("dashboard.cars.create_button")}
              </Button>
            </EmptyState>
          ) : (
            selectedGarageId && (
              <Skeleton visible={isGarageCarsLoading}>
                <Stack gap="xs">
                  <Group justify="flex-end">
                    <TextSerif size="xs" c={textColor}>
                      {t("car.purchase_price")}/{t("car.roi")}
                    </TextSerif>
                  </Group>
                  <ScrollArea
                    h={270}
                    type="auto"
                    scrollbarSize={4}
                    offsetScrollbars
                    styles={{
                      viewport: { paddingRight: cars.length <= 6 ? 0 : 8 },
                    }}
                  >
                    {cars.map((car) => (
                      <Group
                        key={car.identifier}
                        justify="space-between"
                        align="center"
                        gap="sm"
                        wrap="nowrap"
                      >
                        <Flex align="center" gap="sm" flex={1} miw={0}>
                          <Box w={32} style={{ flexShrink: 0 }}>
                            <MakeCard make={car.make} />
                          </Box>

                          <Anchor
                            component={Link}
                            to={paths.app.garage.car.getHref(selectedGarageId, car.identifier)}
                            title={`${car.make} ${car.model} ${car.variant}`}
                            style={{
                              flex: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Text size="sm" fz={fontSize} truncate="end">
                              {car.make} {car.model} {car.variant}
                            </Text>
                          </Anchor>
                        </Flex>

                        <Box ta="right" miw={80} style={{ flexShrink: 0 }}>
                          <Text fz={fontSize}>
                            {formatCurrency(car.purchase_price, currency.code)}
                          </Text>
                          <Text fz={fontSize} c={car.roi > 0 ? "success" : "danger"}>
                            {formatPercentage(car.roi / 100)}
                          </Text>
                        </Box>
                      </Group>
                    ))}
                  </ScrollArea>
                </Stack>
              </Skeleton>
            )
          )}
        </Box>
      </Stack>
    </Card>
  );
};
